import React from 'react';
import { createPortal } from 'react-dom';

import { TOAST_ANIM } from '../../../../theme/tokens';
import { Viewport, Pill, Message, Track, Runner } from './ExportSnackbar.styles';

export type ExportSnackbarProps = {
  /** While true the snackbar shows (it slides out once false). */
  open: boolean;
  /** Its copy (an i18n key). */
  message: string;
};

/**
 * The export's "working on it" snackbar: the studio toast's pill, held up for as long as the export runs, with an
 * indeterminate bar under the copy. Renders into a portal on `document.body`, like the toast.
 */
export const ExportSnackbar = ({ open, message }: ExportSnackbarProps): React.ReactElement | null => {
  // Kept mounted through the exit, then dropped; `on` flips a frame after mounting so the entrance plays.
  const [mounted, setMounted] = React.useState(open);
  const [on, setOn] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setOn(true));
      return () => cancelAnimationFrame(raf);
    }
    setOn(false);
    const timer = window.setTimeout(() => setMounted(false), TOAST_ANIM.EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!mounted) return null;
  return createPortal(
    <Viewport>
      <Pill $on={on} role="status" aria-busy="true">
        <Message text={message} tag="span" />
        <Track aria-hidden="true">
          <Runner />
        </Track>
      </Pill>
    </Viewport>,
    document.body,
  );
};

export default ExportSnackbar;
